export const appPaths = {
  login: '/login',
  acceptInvite: '/accept-invite',
  customerPortal: '/p/:token',
  staffResetPassword: '/staff-reset-password',
  home: '/',
  customers: '/customers',
  customersDetail: (partyId: string) => `/customers/${partyId}` as const,
  organizationCustomers: '/organizacao/clientes',
  organizationCustomersDetail: (partyId: string) => `/organizacao/clientes/${partyId}` as const,
  organizationServices: '/organizacao/catalogo/servicos',
  organizationServiceDetail: (serviceId: string) =>
    `/organizacao/catalogo/servicos/${serviceId}` as const,
  organizationProducts: '/organizacao/catalogo/produtos',
  organizationProductDetail: (productId: string) =>
    `/organizacao/catalogo/produtos/${productId}` as const,
  organizationKits: '/organizacao/catalogo/kits',
  organizationKitDetail: (kitId: string) => `/organizacao/catalogo/kits/${kitId}` as const,
  organizationUsefulLife: '/organizacao/catalogo/vida-util',
  organizationRevisionPlans: '/organizacao/catalogo/planos-de-revisao',
  organizationMaintenanceChecklists: '/organizacao/catalogo/check-lists',
  organizationPricePolicy: '/organizacao/catalogo/precificacao',
  organizationPriceSimulator: '/organizacao/catalogo/simulador',
  organizationPricePromotions: '/organizacao/catalogo/promocoes',
  organizationWorkshops: '/organizacao/oficinas',
  organizationWorkshopDetail: (placeId: string) => `/organizacao/oficinas/${placeId}` as const,
  suppliers: '/suppliers',
  suppliersDetail: (supplierRoleId: string) => `/suppliers/${supplierRoleId}` as const,
  vehicles: '/vehicles',
  vehicleDetail: (customerVehicleId: string) => `/vehicles/${customerVehicleId}` as const,
  /** @deprecated Use vehicleDetail — kept for legacy redirects. */
  customerVehicleDetail: (_partyId: string, customerVehicleId: string) =>
    `/vehicles/${customerVehicleId}` as const,
  catalog: '/catalog',
  catalogServices: '/catalog/services',
  catalogServiceDetail: (serviceId: string) => `/catalog/services/${serviceId}` as const,
  catalogProducts: '/catalog/products',
  catalogProductDetail: (productId: string) => `/catalog/products/${productId}` as const,
  catalogKits: '/catalog/kits',
  catalogKitDetail: (kitId: string) => `/catalog/kits/${kitId}` as const,
  catalogUsefulLife: '/catalog/useful-life',
  catalogRevisionPlans: '/catalog/revision-plans',
  catalogMaintenanceChecklists: '/catalog/maintenance-checklists',
  catalogPricePolicy: '/catalog/price-policy',
  catalogPriceSimulator: '/catalog/price-simulator',
  catalogPricePromotions: '/catalog/price-promotions',
  catalogTaxonomies: '/catalog/taxonomies',
  inventory: '/inventory',
  inventoryStockBalances: '/inventory/stock-balances',
  inventoryStockMovements: '/inventory/stock-movements',
  inventoryStockTransfers: '/inventory/stock-transfers',
  inventoryStockInventories: '/inventory/stock-inventories',
  inventoryInboundConferences: '/inventory/conferencia-entrada',
  inventoryInboundConferenceDetail: (conferenceId: string) =>
    `/inventory/conferencia-entrada/${conferenceId}` as const,
  purchasing: '/purchasing',
  purchasingQuotes: '/purchasing/quotes',
  purchasingQuoteDetail: (purchaseQuoteId: string) =>
    `/purchasing/quotes/${purchaseQuoteId}` as const,
  /** Multi-RFQ workbench (BR-PUR-38) — first id in the path, the others in `?quotes=a,b`. */
  purchasingQuoteWorkbench: (purchaseQuoteId: string, otherPurchaseQuoteIds: readonly string[]) =>
    otherPurchaseQuoteIds.length === 0
      ? (`/purchasing/quotes/${purchaseQuoteId}` as const)
      : (`/purchasing/quotes/${purchaseQuoteId}?quotes=${otherPurchaseQuoteIds.map(encodeURIComponent).join(',')}` as const),
  purchasingQuoteWorkbenchQueryParam: 'quotes',
  purchasingOrders: '/purchasing/orders',
  purchasingOrderDetail: (purchaseOrderId: string) =>
    `/purchasing/orders/${purchaseOrderId}` as const,
  serviceOrderCheckIn: '/check-in',
  serviceOrderCheckInNew: '/check-in/new',
  serviceOrderCheckInResume: (receptionId: string) => `/check-in/${receptionId}` as const,
  serviceOrderQuotes: '/quotes',
  serviceOrderQuoteDetail: (quoteId: string) => `/quotes/${quoteId}` as const,
  serviceOrders: '/service-orders',
  serviceOrderRealizedMargin: '/service-orders/realized-margin',
  serviceOrderBreakEven: '/service-orders/break-even',
  serviceOrderProductivity: '/service-orders/productivity',
  serviceOrderDetail: (serviceOrderId: string) => `/service-orders/${serviceOrderId}` as const,
  serviceOrderCheckOut: '/check-out',
  serviceOrderCheckOutDetail: (serviceOrderId: string) => `/check-out/${serviceOrderId}` as const,
  caixa: '/caixa',
  financeChartOfAccounts: '/financeiro/plano-de-contas',
  financeAccountsPayable: '/financeiro/contas-a-pagar',
  financeAccountsReceivable: '/financeiro/contas-a-receber',
  financeCashFlow: '/financeiro/fluxo-de-caixa',
  financeIncomeStatement: '/financeiro/dre',
  financeInboundChargePolicy: '/financeiro/encargos-entrada',
  financePaymentSettings: '/financeiro/pagamentos',
  financeCollectionCharges: '/financeiro/cobrancas',
  financeBankReconciliation: '/financeiro/conciliacao',
  /** Inventário visual SSOT — única rota harness (showcase por fatia extirpado). */
  harness: '/harness',
  harnessDesignSystem: '/harness/design-system',
  /** Alias estável — mesmo path que `harnessDesignSystem`. */
  designSystem: '/harness/design-system',
  /** Legacy `/design-system` redireciona para `harnessDesignSystem`. */
  designSystemLegacy: '/design-system',
  org: '/org',
  network: (organizationGroupId: string) => `/network/${organizationGroupId}` as const,
  admin: '/admin',
  adminOrganizations: '/admin/organizations',
  adminOrganizationDetail: (id: string) => `/admin/organizations/${id}` as const,
  adminPlaces: '/admin/places',
  adminPlaceDetail: (id: string) => `/admin/places/${id}` as const,
  adminNetworks: '/admin/networks',
  adminNetworkDetail: (id: string) => `/admin/networks/${id}` as const,
  adminCatalogGlobal: '/admin/catalog-global',
  adminCatalogGlobalCuration: '/admin/catalog-curation',
  adminCatalogTaxonomy: '/admin/catalog-taxonomy',
  adminCatalogGeneric: '/admin/catalog-generic',
  adminCatalogGenericServices: '/admin/catalog-generic-services',
  adminCatalogManufacturers: '/admin/catalog-manufacturers',
  adminUsers: '/admin/users',
  adminProfiles: '/admin/profiles',
  adminProfileTemplateNew: '/admin/profiles/new',
  adminProfileTemplateDetail: (id: string) => `/admin/profiles/${id}` as const,
  fiscalParametrizacao: '/fiscal/parametrizacao',
  fiscalImplantacao: '/fiscal/implantacao',
  fiscalDocumentos: '/fiscal/documentos',
  fiscalDocumentoDetail: (documentId: string) => `/fiscal/documentos/${documentId}` as const,
  fiscalDfeInbox: '/fiscal/caixa-entrada',
  fiscalDfeInboxDetail: (documentId: string) => `/fiscal/caixa-entrada/${documentId}` as const,
  /** Reserved — apuração fiscal (nav stub until screen ships). */
  fiscalApuracao: '/fiscal/apuracao',
  /**
   * Legacy place-level settings path — redirects away; config lives under organization workshops
   * (BR-IDT-63).
   */
  placeSettings: '/configuracoes',
  /** Organization-level settings hub (BR-INV-65) — TENANT_ADMIN only. */
  organizationSettings: '/organizacao/configuracoes',
  organizationUsers: '/organizacao/usuarios',
  organizationProfiles: '/organizacao/perfis',
  organizationProfileDetail: (id: string) => `/organizacao/perfis/${id}` as const,
  /** Staff chat inbox — list + thread WhatsApp Web style (TASK-05B). */
  conversations: '/conversations',
  /** WhatsApp WAHA inbox — list + thread for the active place (BR-NOT-45..48). */
  whatsappInbox: '/whatsapp',
  /** Staff drop-off batch for the active place. */
  appointments: '/agenda',
  /** Live yard board of vehicles present at the active place (BR-SO-175..178). */
  yard: '/patio',
  /** Confirm overtime screen — mechanic taps notification deeplink (BR-SO-183). */
  workTimerOvertimeConfirm: (mechanicUserId: string) =>
    `/patio/hora-extra/${mechanicUserId}` as const,
  /** Staff configurable campaigns for the active place (BR-NOT-09). */
  notificationCampaigns: '/campanhas',
  /** Reserved — catálogo NCM global (intent fiscal-ncm-global-catalog; nav stub until screen ships). */
  fiscalNcm: '/fiscal/ncm',
  /** Catálogo geo de cidades (BR-IDT-16 / BR-IDT-17). */
  geoCities: '/cidades',
  adminGeoCities: '/admin/cidades',
} as const;
